import { z } from 'zod';
import {
  canonicalizeCidr,
  cidrsOverlap,
  isDefaultRoute,
  parseCidr,
  parseIp,
  type IpCidr,
} from './ip';

/**
 * WireGuard domain constants, validation, and derivations shared by the controller,
 * the planner, and the console.
 *
 * Design rule (DATA_PLANE.md): AllowedIPs are **encryption selectors only**. They
 * are deliberately not used as the routing or authorization control — dynamic
 * routing (FRR), firewall (nftables), and policy routing enforce those separately.
 * The validators here exist to stop a broad selector from silently becoming a
 * route hijack or a cross-tenant leak.
 */

/** Base64-encoded 32-byte Curve25519 key: 43 payload chars plus one `=` pad. */
export const WG_KEY_PATTERN = /^[A-Za-z0-9+/]{42}[AEIMQUYcgkosw048]=$/;

/** Linux IFNAMSIZ is 16 including the NUL terminator. */
export const WG_INTERFACE_NAME_MAX = 15;
export const WG_INTERFACE_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/** WireGuard requires at least the IPv6 minimum MTU; jumbo frames cap the top. */
export const WG_MTU_MIN = 1280;
export const WG_MTU_MAX = 9000;
export const WG_MTU_DEFAULT = 1420;

/** Default UDP port range the controller allocates listen ports from. */
export const WG_PORT_MIN = 51820;
export const WG_PORT_MAX = 51899;

/** Keepalive bounds; 25s is the conventional value for staying alive through NAT. */
export const WG_KEEPALIVE_MIN = 1;
export const WG_KEEPALIVE_MAX = 65535;
export const WG_KEEPALIVE_DEFAULT = 25;

/**
 * A handshake older than this means the peer is effectively down. WireGuard
 * rekeys every 120s when traffic flows, so 180s is a conservative threshold.
 */
export const WG_HANDSHAKE_STALE_SECONDS = 180;

/** True for a syntactically valid WireGuard public/pre-shared key. */
export function isValidWireGuardKey(key: string): boolean {
  return WG_KEY_PATTERN.test(key);
}

/** True for a name the kernel will accept as an interface name. */
export function isValidInterfaceName(name: string): boolean {
  if (name.length === 0 || name.length > WG_INTERFACE_NAME_MAX) return false;
  if (name === '.' || name === '..') return false;
  return WG_INTERFACE_NAME_PATTERN.test(name);
}

export function isValidPort(port: number): boolean {
  return Number.isInteger(port) && port >= 1 && port <= 65535;
}

export function isValidMtu(mtu: number): boolean {
  return Number.isInteger(mtu) && mtu >= WG_MTU_MIN && mtu <= WG_MTU_MAX;
}

/**
 * TCP MSS to clamp to for a given tunnel MTU. Subtracts the IP + TCP header
 * overhead (20 + 20 for IPv4, 40 + 20 for IPv6) so TCP inside the tunnel stops
 * emitting segments that would need fragmenting.
 */
export function deriveMssClamp(mtu: number, family: 4 | 6 = 4): number {
  return mtu - (family === 4 ? 40 : 60);
}

/**
 * A `host:port` endpoint. The host may be a literal IPv4/IPv6 address or a DNS
 * name (dynamic DNS is supported, so names are resolved on the edge at apply
 * time rather than being frozen into the bundle).
 */
export function parseEndpoint(endpoint: string): { host: string; port: number } | null {
  const trimmed = endpoint.trim();
  // Bracketed IPv6: [2001:db8::1]:51820
  const bracketed = /^\[([^\]]+)\]:(\d{1,5})$/.exec(trimmed);
  if (bracketed) {
    const host = bracketed[1] as string;
    const port = Number(bracketed[2]);
    const addr = parseIp(host);
    if (!addr || addr.family !== 6 || !isValidPort(port)) return null;
    return { host, port };
  }

  const lastColon = trimmed.lastIndexOf(':');
  if (lastColon === -1) return null;
  const host = trimmed.slice(0, lastColon);
  const port = Number(trimmed.slice(lastColon + 1));
  if (!/^\d{1,5}$/.test(trimmed.slice(lastColon + 1)) || !isValidPort(port)) return null;
  if (host === '') return null;
  // A bare IPv6 literal without brackets is ambiguous with host:port — reject it.
  if (host.includes(':')) return null;
  if (parseIp(host)) return { host, port }; // IPv4 literal
  return isValidHostname(host) ? { host, port } : null;
}

export function isValidHostname(host: string): boolean {
  if (host.length === 0 || host.length > 253) return false;
  const labels = host.replace(/\.$/, '').split('.');
  return labels.every((l) => /^[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/.test(l));
}

/**
 * Validate a peer's AllowedIPs as encryption selectors.
 *
 * Rejects a default route outright: `0.0.0.0/0` on a peer would capture every
 * packet the edge sends, hijacking routing decisions that belong to FRR and the
 * policy layer, and would let one peering absorb another tenant's traffic. Also
 * rejects selectors that overlap each other within the same peer, since the
 * kernel resolves overlaps by longest-prefix match and the operator's intent
 * becomes ambiguous.
 */
export function validateAllowedIps(allowedIps: readonly string[]): string[] {
  const errors: string[] = [];
  if (allowedIps.length === 0) {
    errors.push('allowedIps must not be empty');
    return errors;
  }

  const parsed: IpCidr[] = [];
  for (const entry of allowedIps) {
    const cidr = parseCidr(entry);
    if (!cidr) {
      errors.push(`allowedIps: "${entry}" is not a valid CIDR`);
      continue;
    }
    if (isDefaultRoute(cidr)) {
      errors.push(
        `allowedIps: "${entry}" is a default route; AllowedIPs are encryption ` +
          'selectors, not a routing or authorization control',
      );
      continue;
    }
    if (canonicalizeCidr(entry) !== entry.trim()) {
      errors.push(`allowedIps: "${entry}" has host bits set (expected the network address)`);
      continue;
    }
    parsed.push(cidr);
  }

  for (let i = 0; i < parsed.length; i += 1) {
    for (let j = i + 1; j < parsed.length; j += 1) {
      const a = parsed[i] as IpCidr;
      const b = parsed[j] as IpCidr;
      if (cidrsOverlap(a, b)) {
        errors.push(`allowedIps: "${allowedIps[i]}" overlaps "${allowedIps[j]}"`);
      }
    }
  }

  return errors;
}

/**
 * Cross-peer check: within one interface, two peers must not claim overlapping
 * selectors, or the kernel would send a given destination to whichever peer holds
 * the longer prefix — a silent, hard-to-diagnose traffic hijack.
 */
export function findAllowedIpConflicts(
  peers: ReadonlyArray<{ publicKey: string; allowedIps: readonly string[] }>,
): string[] {
  const errors: string[] = [];
  const entries: Array<{ publicKey: string; text: string; cidr: IpCidr }> = [];

  for (const peer of peers) {
    for (const text of peer.allowedIps) {
      const cidr = parseCidr(text);
      if (cidr) entries.push({ publicKey: peer.publicKey, text, cidr });
    }
  }

  for (let i = 0; i < entries.length; i += 1) {
    for (let j = i + 1; j < entries.length; j += 1) {
      const a = entries[i] as (typeof entries)[number];
      const b = entries[j] as (typeof entries)[number];
      if (a.publicKey === b.publicKey) continue;
      if (cidrsOverlap(a.cidr, b.cidr)) {
        errors.push(
          `allowedIps conflict: peer ${a.publicKey.slice(0, 8)}… claims "${a.text}" which ` +
            `overlaps "${b.text}" claimed by peer ${b.publicKey.slice(0, 8)}…`,
        );
      }
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Zod schemas (API + desired-state validation)
// ---------------------------------------------------------------------------

export const wireGuardKeySchema = z
  .string()
  .refine(isValidWireGuardKey, 'must be a base64-encoded 32-byte WireGuard key');

export const interfaceNameSchema = z
  .string()
  .refine(
    isValidInterfaceName,
    `must be a valid interface name (max ${WG_INTERFACE_NAME_MAX} chars)`,
  );

export const cidrSchema = z.string().refine((v) => parseCidr(v) !== null, 'must be a valid CIDR');

export const endpointSchema = z
  .string()
  .refine((v) => parseEndpoint(v) !== null, 'must be host:port (bracket IPv6 literals)');

/** The `wireguard` resource spec rendered into a revision and applied by the edge. */
export const wireGuardInterfaceSpecSchema = z.object({
  name: interfaceNameSchema,
  listenPort: z.number().int().min(1).max(65535),
  addresses: z.array(cidrSchema).min(1),
  mtu: z.number().int().min(WG_MTU_MIN).max(WG_MTU_MAX).optional(),
  mssClamp: z.number().int().positive().optional(),
  /** Public key of the interface's own active key (private key stays on the edge). */
  publicKey: wireGuardKeySchema,
  peers: z
    .array(
      z.object({
        publicKey: wireGuardKeySchema,
        allowedIps: z.array(cidrSchema).min(1),
        endpoint: endpointSchema.optional(),
        persistentKeepalive: z
          .number()
          .int()
          .min(WG_KEEPALIVE_MIN)
          .max(WG_KEEPALIVE_MAX)
          .optional(),
        /** Set when a PSK is configured; the value itself is delivered separately. */
        usePresharedKey: z.boolean().optional(),
      }),
    )
    .default([]),
});
export type WireGuardInterfaceSpec = z.infer<typeof wireGuardInterfaceSpecSchema>;

/** Per-peer status the edge reports back from `wg show`. */
export interface WireGuardPeerStatus {
  publicKey: string;
  endpoint?: string;
  lastHandshakeUnix?: number;
  rxBytes?: number;
  txBytes?: number;
}

/** Per-interface status the edge reports back from `wg show`. */
export interface WireGuardInterfaceStatusReport {
  name: string;
  publicKey?: string;
  listenPort?: number;
  up: boolean;
  peers: WireGuardPeerStatus[];
}
